import { useState, useRef, useCallback, useEffect } from "react";
import insurancePolicyService from "../lib/InsurancePolicyService";

// Maps SSE phases to upload stage IDs used by the UI
const PHASE_TO_STAGE = {
  ocr: "parsing",
  quick_extract: "extracting",
  full_extract: "organizing",
};

const STREAM_TIMEOUT_MS = 120_000; // 2 minutes — matches server-side SseEmitter timeout

/**
 * Hook that manages an SSE extraction stream lifecycle.
 *
 * Returns reactive state driven by real server events,
 * with graceful fallback on error/timeout.
 */
export function useExtractionStream() {
  const [stage, setStage] = useState(null);
  const [message, setMessage] = useState(null);
  const [extractedData, setExtractedData] = useState({
    quick: null,
    full: null,
  });
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState(null);
  const [completed, setCompleted] = useState(false);

  const abortRef = useRef(null);
  const timeoutRef = useRef(null);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      abortRef.current?.abort();
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  const startStream = useCallback((policyId, firebaseToken, streamEndpoint = null) => {
    // Reset state
    setError(null);
    setCompleted(false);
    setIsStreaming(true);
    setExtractedData({ quick: null, full: null });

    // Timeout fallback — if server never sends "complete", auto-resolve
    timeoutRef.current = setTimeout(() => {
      console.warn("Extraction stream timed out — resolving gracefully");
      abortRef.current?.abort();
      setIsStreaming(false);
      setCompleted(true);
    }, STREAM_TIMEOUT_MS);

    const clearTimeoutFallback = () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };

    const handle = insurancePolicyService.streamExtraction(
      policyId,
      firebaseToken,
      {

        onStatus: (event) => {
          const newStage = PHASE_TO_STAGE[event.phase];
          if (newStage) setStage(newStage);
          if (event.message) setMessage(event.message);
        },

        onOcrComplete: (event) => {
          setStage("extracting");
          if (event.message) setMessage(event.message);
        },

        onQuickExtract: (event) => {
          setStage("extracting");
          if (event.message) setMessage(event.message);
          if (event.data) {
            setExtractedData((prev) => ({ ...prev, quick: event.data }));
          }
        },

        onFullExtract: (event) => {
          setStage("organizing");
          if (event.message) setMessage(event.message);
          if (event.data) {
            setExtractedData((prev) => ({ ...prev, full: event.data }));
          }
        },

        onComplete: (event) => {
          clearTimeoutFallback();
          if (event.message) setMessage(event.message);
          setIsStreaming(false);
          setCompleted(true);
        },

        onError: (event) => {
          clearTimeoutFallback();
          console.error("Extraction stream error:", event);
          setError(event.message || "Extraction failed");
          // Graceful fallback — treat as success so user can proceed
          setIsStreaming(false);
          setCompleted(true);
        },
      },
      streamEndpoint
    );

    abortRef.current = handle;
  }, []);

  const abort = useCallback(() => {
    abortRef.current?.abort();
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setIsStreaming(false);
  }, []);

  return {
    startStream,
    abort,
    stage,
    message,
    extractedData,
    isStreaming,
    error,
    completed,
  };
}
